import React, { useCallback, useState } from "react"
import { useMutation } from "@apollo/client/react"
import { toast } from "sonner"
import { ToggleResourceEngagementMutation } from "@love-irish/graphql-api"
import { useSession } from "@love-irish/application-session-provider"

export interface UseUsingToggleOptions {
  /** GraphQL resource type, e.g. "Item::App", "Collection". */
  resourceType: string
  /** The resource's global id (gid). */
  resourceId: string
  /** Display name, used in the toast copy (falls back to a generic noun). */
  name?: string
  /** Server-sanitized, plain-text name (markdown already stripped). Preferred for the toast so it does
   *  no per-toast markdown parsing; falls back to `name`. */
  sanitizedName?: string
  /** Initial "using" state, from the server (`currentProfileUsing`). */
  initialUsing?: boolean
  /** Called when a logged-out user attempts to toggle — e.g. open an upsell modal. */
  onRequireAuth?: () => void
}

export interface UseUsingToggleResult {
  /** Current (optimistic) "using" state. */
  using: boolean
  /** Whether the toggle mutation is in flight. */
  loading: boolean
  /** Toggle using/not-using. No-ops into `onRequireAuth` when logged out. */
  toggle: () => Promise<void>
}

/**
 * App-wide "I'm using this / stop using this" logic: the ProfileEngagement(:using) toggle mutation,
 * optimistic state with rollback on error, and consistent success/error toasts. UI-agnostic — pair it
 * with any button or menu item. Logged-out attempts call `onRequireAuth` instead of mutating, so each
 * surface supplies its own auth prompt.
 *
 * Mirrors `@love-irish/resource-use-save-toggle`, but for the `using` engagement type — the same
 * `ToggleResourceEngagement` mutation, just a different `engagementType`.
 *
 * ```tsx
 * const { using, loading, toggle } = useUsingToggle({
 *   resourceType: item.type,
 *   resourceId: item.gid,
 *   name: item.name,
 *   initialUsing: item.currentProfileUsing,
 *   onRequireAuth: () => setUpsellOpen(true),
 * })
 * ```
 */
export function useUsingToggle({
  resourceType,
  resourceId,
  name,
  sanitizedName,
  initialUsing = false,
  onRequireAuth,
}: UseUsingToggleOptions): UseUsingToggleResult {
  const { loggedIn } = useSession()
  const [using, setUsing] = useState(initialUsing)
  const [toggleEngagement, { loading }] = useMutation(
    ToggleResourceEngagementMutation
  )

  const toggle = useCallback(async () => {
    if (!loggedIn) {
      onRequireAuth?.()
      return
    }

    // Optimistic flip; revert on error.
    const next = !using
    setUsing(next)

    // Names can contain simple markdown (e.g. "Lá Fhéile Pádraig _(St. Patrick's Day)_"). Use the
    // server-sanitized plain name so the toast does no per-toast markdown parsing.
    const displayName = sanitizedName ?? name
    const label: React.ReactNode = displayName ? (
      <>
        &ldquo;<strong className="font-sans font-bold">{displayName}</strong>
        &rdquo;
      </>
    ) : (
      "this"
    )

    try {
      await toggleEngagement({
        variables: { resourceType, resourceId, engagementType: "using" },
      })

      if (next) {
        toast.success(<>You&rsquo;re using {label}</>, {
          description: "It'll show up for others as something you use.",
        })
      } else {
        toast(<>No longer using {label}</>)
      }
    } catch (err) {
      setUsing(!next)
      toast.error("Couldn't update your list. Please try again.")
      // eslint-disable-next-line no-console
      console.error("Failed to toggle using:", err)
    }
  }, [
    loggedIn,
    using,
    name,
    sanitizedName,
    resourceType,
    resourceId,
    toggleEngagement,
    onRequireAuth,
  ])

  return { using, loading, toggle }
}

export default useUsingToggle
